/**
 * Error classification utilities
 * Centralizes error type detection for consistent error handling across the application
 */

/**
 * Check if error is a 404 (not found) error
 * Detects 404 errors from various error formats (API responses, fetch errors, etc.)
 *
 * @param error - Error to check
 * @returns true if error is a 404 error
 */
export function is404Error(error: unknown): boolean {
	if (typeof error === "object" && error !== null) {
		const err = error as { http_status?: number; status?: number; response?: { status?: number }; message?: string };
		return (
			err.http_status === 404 ||
			err.status === 404 ||
			err.response?.status === 404 ||
			(typeof err.message === "string" && (err.message.includes("404") || err.message.includes("not found") || err.message.includes("Not found")))
		);
	}
	return false;
}

function getAuthStatusFromObject(err: { http_status?: number; status?: number; response?: { status?: number }; message?: string }): number | undefined {
	if (err.http_status === 401 || err.http_status === 403) return err.http_status;
	if (err.status === 401 || err.status === 403) return err.status;
	if (err.response?.status === 401 || err.response?.status === 403) return err.response.status;
	return undefined;
}

function hasAuthMessage(message: string): boolean {
	return message.includes("Unauthorized") || message.includes("Forbidden");
}

/**
 * Check if error is an authentication/authorization error (401 or 403)
 * Detects 401/403 errors from various error formats
 *
 * @param error - Error to check
 * @returns true if error is a 401 or 403 error
 */
export function isAuthError(error: unknown): boolean {
	if (typeof error !== "object" || error === null) return false;
	const errorObj = error as { http_status?: number; status?: number; response?: { status?: number }; message?: string };
	if (getAuthStatusFromObject(errorObj) !== undefined) return true;
	return typeof errorObj.message === "string" && hasAuthMessage(errorObj.message);
}

const NETWORK_PATTERNS = [
	"Failed to fetch",
	"ERR_CONNECTION_REFUSED",
	"ERR_CONNECTION_RESET",
	"ERR_PROXY_CONNECTION_FAILED",
	"ERR_FAILED",
	"NetworkError",
	"network",
];

function messageHasNetworkPattern(text: string): boolean {
	return NETWORK_PATTERNS.some((p) => text.includes(p));
}

function errorNameIsNetworkOrType(name: string): boolean {
	return name === "NetworkError" || name === "TypeError";
}

/**
 * Check if error is a network error (temporary, can be retried)
 * Detects network errors like connection failures, timeouts, etc.
 *
 * @param error - Error to check
 * @returns true if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
	if (error instanceof Error) {
		return messageHasNetworkPattern(error.message) || errorNameIsNetworkOrType(error.name);
	}
	if (typeof error === "string") {
		return NETWORK_PATTERNS.slice(0, 5).some((p) => error.includes(p));
	}
	return false;
}

/**
 * Check if error is a 429 (Too Many Requests) error
 * Detects rate limiting errors from various error formats
 *
 * @param error - Error to check
 * @returns true if error is a 429 error
 */
export function is429Error(error: unknown): boolean {
	if (typeof error === "object" && error !== null) {
		const err = error as { http_status?: number; status?: number; response?: { status?: number }; message?: string };
		return (
			err.http_status === 429 ||
			err.status === 429 ||
			err.response?.status === 429 ||
			(typeof err.message === "string" &&
				(err.message.includes("429") || err.message.includes("Too Many Requests") || err.message.includes("rate limit")))
		);
	}
	return false;
}

function is5xxStatus(status: number): boolean {
	return status >= 500 && status < 600;
}

function getStatusFromError(error: unknown): number | undefined {
	if (typeof error !== "object" || error === null) return undefined;
	const err = error as { http_status?: number; status?: number; response?: { status?: number } };
	return err.http_status ?? err.status ?? err.response?.status;
}

/**
 * Check if error is retryable (network errors, temporary server errors)
 * Determines if an error should trigger a retry mechanism
 *
 * @param error - Error to check
 * @returns true if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
	if (is404Error(error) || isAuthError(error)) return false;
	if (is429Error(error) || isNetworkError(error)) return true;
	const status = getStatusFromError(error);
	return status !== undefined && is5xxStatus(status);
}

/**
 * Blob missing on disk: HTTP 404 (current) or legacy 5xx (ENOENT before LocalBlobMissingError mapping).
 */
export function isBlobUnavailableDownloadError(error: unknown): boolean {
	if (is404Error(error)) {
		return true;
	}
	const status = getStatusFromError(error);
	return status !== undefined && is5xxStatus(status);
}
